import { Component, Input, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ContactGroupService } from 'src/app/private/services/contact-group.service';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-call-campaign-details',
  imports: [SharedModule],
  templateUrl: './call-campaign-details.component.html',
  styleUrl: './call-campaign-details.component.scss'
})
export class CallCampaignDetailsComponent {


  dataSource: any[] = [];
  currentSelectedCampaignID: string | null = null;

  @Input() toggleFlag: boolean = false;

  totalRecords: number = 0;
  rows: number = 50; // Default page size
  pageSizeOptions = [5, 10, 20, 50];
  loading: boolean = false;

  displayedColumns: Array<{ field: string, header: string, type: string }> = [];
  dataToEdit: any = {};

  campaignName: string = '';

  constructor(
    private api: ContactGroupService,
    private localStorage: LocalStorageService,
    private messageService: MessageService,
    private route : ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.setDisplayColumns();
    
    this.currentSelectedCampaignID = this.route.snapshot.paramMap.get('id');
    // 
    this.route.queryParams.subscribe(params => {
      const type = params['name'];
      if (type) {
        this.campaignName = type;
      }
    });
    this.loadData({ first: 0, rows: this.rows });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentSelectedCampaignID']?.currentValue
      || changes['toggleFlag']?.currentValue
    ) {
      this.loadData({ first: 0, rows: this.rows });
    }
  }

  setDisplayColumns(): void {
    this.displayedColumns = [
      { field: 'name', header: 'Contact Name', type: 'contact_name' },
      { field: 'mobile_number', header: 'Mobile Number', type: 'phone' },
      { field: 'email', header: 'Email', type: 'email' },
      { field: 'call_status', header: 'Call Status', type: 'call_status' },
      { field: 'course', header: 'Course', type: 'course' },
      { field: 'advisor_guidance', header: 'Advisor Notes', type: 'advisor_notes' },
      { field: 'call_type', header: 'Call Type', type: 'call_type' },
      { field: 'call_action_type', header: 'Call Action', type: 'call_action' },
      { field: 'call_remarks', header: 'Call Remarks', type: 'call_remarks' },
      { field: 'call_pickup_date', header: 'Pickup Date', type: 'pickup_date' },
      { field: 'call_pickup_time', header: 'Pickup Time', type: 'pickup_time' },
      { field: 'num_tries', header: 'Attempts', type: 'attempts' },
      { field: 'follow_up_date', header: 'Follow Up Date', type: 'follow_up_date' },
      { field: 'follow_up_time', header: 'Follow Up Time', type: 'follow_up_time' },
      { field: 'last_updated', header: 'Last Updated', type: 'last_updated' },
      { field: 'created_at', header: 'Created On', type: 'created_at' },
    ];
  }

  loadData(event?: any) {


    if (!this.currentSelectedCampaignID) {
      return;
    }

    // const payload = {
    //   where: {
    //     organization_id: this.localStorage.getOrganizationId(),
    //     contact_group_id: this.currentSelectedCampaignID
    //   },
    //   filter: null,
    //   page,
    //   limit: size,
    //   order: {
    //     col: '"contact_group_list"."updatedAt"',
    //     dir: 'DESC'
    //   }
    // };

    this.loading = true;

    if (!this.currentSelectedCampaignID) {
      return;
    }

    const payload = {
      organizationId: this.localStorage.getOrganizationId(),
      campusId: this.localStorage.getCampusId(), // make sure you have this
      campaignId :  this.currentSelectedCampaignID,
    }

    this.api.getContactsReportInDynamoDBGroup(payload).subscribe({
      next: (data) => {
        this.dataSource = data.contacts || [];
        this.totalRecords = data.total_contacts || 0;
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load contact groups.' });
        this.loading = false;
      },
    });
  }

  /**
   * Refresh the data by calling the API again
   */
  refreshData(): void {
    this.loadData({ first: 0, rows: this.rows });
  }

  setTemplateEditData(template: any): void {
    this.dataToEdit = template;
  }

  getFullName(user: any): string {
    return [user?.prefix, user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(' ') || 'N/A';
  }

  formatTimestamp(timestamp: number): Date | null {
    return timestamp ? new Date(timestamp) : null;
  }

  getCallStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'dialed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      'busy': 'bg-yellow-100 text-yellow-800',
      'answered': 'bg-blue-100 text-blue-800',
      'no_answer': 'bg-gray-100 text-gray-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  getCallActionClass(action: string): string {
    const actionClasses: { [key: string]: string } = {
      'interested': 'bg-green-100 text-green-800',
      'not_interested': 'bg-red-100 text-red-800',
      'wrong_respond': 'bg-orange-100 text-orange-800',
      'callback_requested': 'bg-blue-100 text-blue-800',
      'no_response': 'bg-gray-100 text-gray-800'
    };
    return actionClasses[action] || 'bg-gray-100 text-gray-800';
  }

  formateStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'dialed': 'Dialed',
      'just_called': 'Called',
      'failed': 'Failed',
      'busy': 'Busy',
      'answered': 'Answered',
      'no_answer': 'No Answer',
      'completed': 'Completed',
      'wrong_user': 'Wrong Number',
    };
    return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
  }

  /**
   * Export all table data to Excel
   */
  exportToExcel(): void {
    if (!this.dataSource || this.dataSource.length === 0) {
      this.messageService.add({ 
        severity: 'warn', 
        summary: 'No Data', 
        detail: 'No data available to export.' 
      });
      return;
    }

    try {
      // Prepare data for export
      const exportData = this.dataSource.map((row, index) => {
        const exportRow: any = {
          'S.No': index + 1,
          'Contact Name': row['name'] || 'N/A',
          'Mobile Number': row['mobile_number'] || 'N/A',
          'Email': row['email'] || 'N/A',
          'Call Status': row['call_status'] ? this.formateStatus(row['call_status']) : 'N/A',
          'Course': row['course'] || 'N/A',
          'Advisor Notes': row['advisor_guidance'] || 'N/A',
          'Call Type': row['call_type'] ? row['call_type'].replace('_', ' ').toUpperCase() : 'N/A',
          'Call Action': row['call_action_type'] ? row['call_action_type'].replace('_', ' ').toUpperCase() : 'N/A',
          'Call Remarks': row['call_remarks'] ? row['call_remarks'].replace('-', ' - ') : 'N/A',
          'Pickup Date': row['call_pickup_date'] || 'N/A',
          'Pickup Time': row['call_pickup_time'] || 'N/A',
          'Attempts': row['num_tries'] || 0,
          'Follow Up Date': row['follow_up_date'] || 'N/A',
          'Follow Up Time': row['follow_up_time'] || 'N/A',
          'Last Updated': row['last_updated'] ? new Date(row['last_updated']).toLocaleString() : 'N/A',
          'Created On': row['created_at'] ? new Date(row['created_at']).toLocaleDateString() : 'N/A'
        };
        return exportRow;
      });

      // Create worksheet
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 8 },  // S.No
        { wch: 20 }, // Contact Name
        { wch: 15 }, // Mobile Number
        { wch: 25 }, // Email
        { wch: 15 }, // Call Status
        { wch: 20 }, // Course
        { wch: 30 }, // Advisor Notes
        { wch: 15 }, // Call Type
        { wch: 18 }, // Call Action
        { wch: 25 }, // Call Remarks
        { wch: 15 }, // Pickup Date
        { wch: 12 }, // Pickup Time
        { wch: 10 }, // Attempts
        { wch: 15 }, // Follow Up Date
        { wch: 12 }, // Follow Up Time
        { wch: 20 }, // Last Updated
        { wch: 15 }  // Created On
      ];
      ws['!cols'] = columnWidths;

      // Create workbook
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Campaign Details');

      // Generate filename with campaign name and timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const campaignNameForFile = this.campaignName ? this.campaignName.replace(/[^a-z0-9]/gi, '_') : 'Campaign';
      const fileName = `${campaignNameForFile}_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);

      // this.messageService.add({ 
      //   severity: 'success', 
      //   summary: 'Success', 
      //   detail: 'Data exported successfully!' 
      // });
    } catch (error) {
      console.error('Export error:', error);
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Export Failed', 
        detail: 'Failed to export data. Please try again.' 
      });
    }
  }
}